import Icon from '@ant-design/icons';
import { Button, Table, Tooltip } from 'antd';
import Search from 'antd/lib/input/Search';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import connectionApi from '../../../api/ConnectionApi';
import LayoutComponent from '../../../components/LayoutComponent/LayoutComponent';
import { Page } from '../../../model/elements';
import { Connection } from '../../../model/entities';
import { ReactComponent as AddSvg } from '../../../resources/images/add.svg';
import notificationService from '../../../services/NotificationService';
import tableService from '../../../services/TableService';
import { ConnectionType } from '../../../model/types';

/**
 * Returns the connections permissions page.
 * @returns the connections permissions page.
 */
const ConnectionPermissionsPage = (): React.ReactElement => {
    /*** HOOKS ***/

    const intl = useIntl();
    const [connectionPermissionsPage, setConnectionPermissionsPage] = useState<Page<Connection>>();
    const [loading, setLoading] = useState<boolean>();
    const [searchText, setSearchText] = useState<string>();
    const connectionType: ConnectionType = 'PERMISSION';

    /*** EFFECTS ***/

    useEffect(() => {
        const init = async () => {
            try {
                setLoading(true);

                const connectionPermissionsPage = await connectionApi.list(
                    0,
                    tableService.pageSize,
                    'name',
                    true,
                    connectionType,
                );

                setConnectionPermissionsPage(connectionPermissionsPage);
            } catch (error) {
                notificationService.displayError(error, intl);
            } finally {
                setLoading(false);
            }
        };
        init();
    }, [intl]);

    /*** METHODS ***/

    const list = async (pagination: TablePaginationConfig, filters: any, sorter: any) => {
        try {
            setLoading(true);
            const page = pagination.current!;
            const pageSize = pagination.pageSize!;
            const sortField = sorter.field;
            const sortOrder = sorter.order === 'ascend';
            const connectionPermissionsPage = await connectionApi.list(
                page - 1,
                pageSize,
                sortField,
                sortOrder,
                connectionType,
                searchText,
            );
            setConnectionPermissionsPage(connectionPermissionsPage);
        } catch (error) {
            notificationService.displayError(error, intl);
        } finally {
            setLoading(false);
        }
    };

    const search = async (searchText: string) => {
        try {
            setLoading(true);

            const pageSize = connectionPermissionsPage!.size;
            const sortField = connectionPermissionsPage!.sort.field!;
            const sortOrder = connectionPermissionsPage!.sort.order!;
            const connectionsPageNew = await connectionApi.list(
                0,
                pageSize,
                sortField,
                sortOrder,
                connectionType,
                searchText,
            );

            setConnectionPermissionsPage(connectionsPageNew);
            setSearchText(searchText);
        } catch (error) {
            notificationService.displayError(error, intl);
        } finally {
            setLoading(false);
        }
    };

    /*** COMPONENTS ***/

    const items = connectionPermissionsPage ? connectionPermissionsPage.content : [];
    const columns: ColumnsType<Connection> = [
        {
            title: <FormattedMessage id="connectionPermission.name" />,
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            defaultSortOrder: 'ascend',
            width: 200,
            render: (value: string, connection: Connection) => (
                <Link to={`/sync/permissions/${connection.id}`}>{value}</Link>
            ),
        },
        {
            title: <FormattedMessage id="connectionPermission.description" />,
            dataIndex: 'description',
            key: 'description',
            sorter: true,
            width: 300,
            ellipsis: true,
            render: (value: string, connection: Connection) => (
                <Link to={`/sync/permissions/${connection.id}`}>{value}</Link>
            ),
        },
        {
            title: <FormattedMessage id="connectionPermission.audit.creator" />,
            dataIndex: 'audit.creator.firstName',
            key: 'creator',
            sorter: true,
            width: 150,
            render: (value: string, connection: Connection) => (
                <Link to={`/sync/permissions/${connection.id}`}>
                    {connection.audit?.creator.firstName} {connection.audit?.creator.lastName}
                </Link>
            ),
        },
        {
            title: <FormattedMessage id="connectionPermission.type" />,
            dataIndex: 'source.provider',
            key: 'type',
            sorter: true,
            align: 'center',
            width: 150,
            render: (value: string, connection: Connection) => (
                <Link to={`/sync/permissions/${connection.id}`}>
                    {connection.source?.provider} &gt; {connection.target?.provider}
                </Link>
            ),
        },
        {
            title: <FormattedMessage id="connectionPermission.source" />,
            dataIndex: 'source.name',
            key: 'source',
            sorter: true,
            align: 'center',
            width: 150,
            render: (value: any, connection: Connection) => (
                <Link to={`/sync/permissions/${connection.id}`}>{connection.source?.name}</Link>
            ),
        },
        {
            title: <FormattedMessage id="connectionPermission.target" />,
            dataIndex: 'target.name',
            key: 'target',
            sorter: true,
            align: 'center',
            width: 150,
            render: (value: any, connection: Connection) => (
                <Link to={`/sync/permissions/${connection.id}`}>{connection.target?.name}</Link>
            ),
        },
    ];

    const placeholder: string = intl.formatMessage({ id: 'connectionPermissions.search' });

    return (
        <LayoutComponent pageId="connectionPermissions">
            <div className="toolbar">
                <Search placeholder={placeholder} onSearch={search} size="large" className="search" />
                <Link to="/sync/permissions/new">
                    <Tooltip title={<FormattedMessage id="connectionPermissions.new.tooltip" />}>
                        <Button type="primary" size="large" icon={<Icon component={AddSvg} />}>
                            <FormattedMessage id="connectionPermissions.new" tagName="span" />
                        </Button>
                    </Tooltip>
                </Link>
            </div>
            <Table
                dataSource={items}
                columns={columns}
                pagination={tableService.createPagination(connectionPermissionsPage)}
                rowKey="id"
                onChange={list}
                sortDirections={['ascend', 'descend']}
                showSorterTooltip={false}
                loading={loading}
            />
        </LayoutComponent>
    );
};

export default ConnectionPermissionsPage;
